import { getNewSearchResult } from "../../Renderers/Views/GlobalSearchRenderers/search.healper";
import {
  GLOBAL_SEARCH,
  SEARCH_STRINGS,
  SEARCH_KEYS,
  ENTITY_DEFAULTS,
  ENTITY_ORDER,
  RECORD_FIELDS,
  HIGHLIGHT_FIELDS,
  DEFAULT_HIGHLIGHT_FIELDS,
  RECENT,
} from "./M_GlobalSearch.config";

export const capitalize = (value = "") =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

export const isSearchHotkey = (event) =>
  (event.ctrlKey || event.metaKey) &&
  !event.shiftKey &&
  !event.altKey &&
  SEARCH_KEYS.HOTKEY_LETTERS.includes(event.key);

const routeSegments = (value = "") =>
  value.split("?")[0].split("#")[0].replace(/^\/+/, "").toLowerCase().split("/");

export const isSamePage = (route = "") => {
  if (typeof window === "undefined" || !route) {
    return false;
  }
  return routeSegments(route)[0] === routeSegments(window.location.pathname)[0];
};

export const isSameCrm = (route = "") => {
  if (typeof window === "undefined" || !route) {
    return false;
  }
  const target = routeSegments(route);
  const current = routeSegments(window.location.pathname);
  return target[0] === current[0] && (target[1] || "") === (current[1] || "");
};


const readTemplates = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(GLOBAL_SEARCH.TEMPLATE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const templateIdFor = (templates, pageUrl) => {
  const match = templates.filter(
    (template) => template && template.label === pageUrl
  );
  return match.length > 0 && match[0].templateId ? match[0].templateId : "";
};

const legacyRoute = (row, templates) => {
  if (!row.pageId || !row.id || !row.pageUrl) {
    return "";
  }
  return "/" + row.pageUrl + "/" + row.id + "/" + templateIdFor(templates, row.pageUrl);
};

const toChipText = (chips) => {
  if (Array.isArray(chips)) {
    return chips.filter(Boolean).join(", ");
  }
  return chips || "";
};

const fromLegacyRecord = (templates) => (row = {}) => ({
  id: row.id,
  heading: row.title || "",
  subHeading: row.email || "",
  entityType: row.pageUrl || "",
  tag: toChipText(row.chips),
  route: legacyRoute(row, templates),
});

let requestSeq = 0;

export const cancelGlobalSearch = () => {
  requestSeq += 1;
};

export const fetchGlobalSearchResults = async (keyword) => {
  const seq = (requestSeq += 1);
  let rows;
  try {
    rows = await getNewSearchResult(keyword);
  } catch (error) {
    return { error: error || true };
  }
  if (seq !== requestSeq) {
    return { cancelled: true };
  }
  const list = Array.isArray(rows) ? rows : [];
  return { data: list.map(fromLegacyRecord(readTemplates())) };
};

const entityOrderIndex = (key) => {
  const index = ENTITY_ORDER.indexOf(key);
  return index === -1 ? ENTITY_ORDER.length : index;
};

export const buildGroups = (results = []) => {
  const order = [];
  const grouped = {};
  results.forEach((record) => {
    const key = (record.entityType || ENTITY_DEFAULTS.KEY).toLowerCase();
    if (!grouped[key]) {
      grouped[key] = {
        key,
        label: capitalize(record.entityType || ENTITY_DEFAULTS.LABEL),
        items: [],
      };
      order.push(key);
    }
    grouped[key].items.push(record);
  });
  return order
    .map((key) => grouped[key])
    .sort((a, b) => entityOrderIndex(a.key) - entityOrderIndex(b.key));
};

export const getHighlightConfig = (item = {}) => {
  const key = (item.entityType || item.iconKey || "").toLowerCase();
  const fields = HIGHLIGHT_FIELDS[key] || DEFAULT_HIGHLIGHT_FIELDS;
  return {
    heading: fields.includes(RECORD_FIELDS.HEADING),
    subHeading: fields.includes(RECORD_FIELDS.SUB_HEADING),
  };
};

export const buildTabs = (groups = []) => [
  {
    key: GLOBAL_SEARCH.ALL_TAB,
    label: SEARCH_STRINGS.ALL_TAB,
    count: groups.reduce((sum, group) => sum + group.items.length, 0),
  },
  ...groups.map((group) => ({
    key: group.key,
    label: group.label,
    count: group.items.length,
  })),
];

export const flattenGroups = (groups = []) =>
  groups.reduce((all, group) => all.concat(group.items), []);

const recentKeyOf = (item = {}) => item.route || item.id || "";

export const getRecentSearches = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return [];
  }
  try {
    const stored = window.localStorage.getItem(RECENT.STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed.slice(0, RECENT.MAX) : [];
  } catch (error) {
    return [];
  }
};

export const addRecentSearch = (item) => {
  if (typeof window === "undefined" || !window.localStorage || !item) {
    return getRecentSearches();
  }
  const key = recentKeyOf(item);
  if (!key) {
    return getRecentSearches();
  }
  const entry = {
    id: item.id,
    heading: item.heading,
    subHeading: item.subHeading,
    entityType: item.entityType,
    tag: item.tag,
    route: item.route,
  };
  const next = [
    entry,
    ...getRecentSearches().filter((row) => recentKeyOf(row) !== key),
  ].slice(0, RECENT.MAX);
  try {
    window.localStorage.setItem(RECENT.STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    return getRecentSearches();
  }
  return next;
};

export const clearRecentSearches = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return [];
  }
  try {
    window.localStorage.removeItem(RECENT.STORAGE_KEY);
  } catch (error) {
    return [];
  }
  return [];
};

export const buildRecentGroup = (recent = []) =>
  recent.length
    ? [{ key: RECENT.GROUP_KEY, label: SEARCH_STRINGS.RECENT, items: recent }]
    : [];

export const splitHighlight = (text = "", query = "") => {
  const source = String(text);
  const term = String(query).trim();
  if (!term || !source) {
    return [{ text: source, isMatch: false }];
  }
  const lowerSource = source.toLowerCase();
  const lowerTerm = term.toLowerCase();
  const segments = [];
  let cursor = 0;
  let index = lowerSource.indexOf(lowerTerm);
  while (index !== -1) {
    if (index > cursor) {
      segments.push({ text: source.slice(cursor, index), isMatch: false });
    }
    segments.push({
      text: source.slice(index, index + term.length),
      isMatch: true,
    });
    cursor = index + term.length;
    index = lowerSource.indexOf(lowerTerm, cursor);
  }
  if (cursor < source.length) {
    segments.push({ text: source.slice(cursor), isMatch: false });
  }
  return segments;
};
